import { apiUpdateInspectionFiltered } from "@app/api/inspections/inspections.api";
import {
  ResultType,
} from "@app/constants";
import type { ItemDraft } from "./createInspectionForm.types";
import type { LatestInspectionItem } from "./createInspectionForm.types";
import {
  type CreateInspectionTemplateItem,
  type MachineReportMediaPayload,
  type MachineReportPayload,
  type MachineReportRecordingPayload,
  normalizeNumericResultForPayload,
  resolveItemResult,
  shouldIncludeMachineReport,
  uploadMediaFiles,
  uploadRecording,
  uploadSignature,
} from "./createInspectionSubmit.helper";

export type ItemDraftBaseline = {
  result: string;
  comment: string;
  freeText: string;
  mediaUrls: string[];
  recordingUrl: string | null;
};

export type UpdateInspectionItemPayload = {
  inspectionItemId: string;
  result: string;
  isRequired: boolean;
  resultType: string;
  isItemUpdate: boolean;
  itemCode?: string | null;
  freeText?: string;
  machineReport?: MachineReportPayload | null;
  inspectionResultId?: string;
};

export type UpdateInspectionPayload = {
  inspectionFormId: string;
  currentStatus: string;
  inspectionItems: UpdateInspectionItemPayload[];
  inspectionAt?: string;
  lastUpdatedSignatureUrl?: string;
};

type ExistingMediaFile = File & {
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  captureMediaAt?: string;
  filePath?: string;
};

function isNumericItem(item: CreateInspectionTemplateItem): boolean {
  return (
    item.resultType === ResultType.NUMERIC ||
    item.resultType === ResultType.ODOMETER
  );
}

function extractFilePathFromUrl(url?: string | null): string {
  if (!url) return "";
  const match = url.match(/contents\/([^?]+)/i);
  if (match?.[1]) {
    return decodeURIComponent(match[1]);
  }
  return url;
}

function extractFileNameFromUrl(url?: string | null): string {
  if (!url) return "";
  const path = extractFilePathFromUrl(url);
  const parts = path.split("/");
  return parts[parts.length - 1] || "";
}

function getComparableResult(
  item: CreateInspectionTemplateItem,
  draft: ItemDraft | undefined,
): string {
  const result = resolveItemResult(item, draft);
  if (isNumericItem(item)) {
    return normalizeNumericResultForPayload(draft?.numericValue ?? "", item);
  }
  return result;
}

function getDraftMediaUrls(draft: ItemDraft): string[] {
  return draft.mediaFiles
    .map((file) => {
      const media = file as ExistingMediaFile;
      return media.mediaUrl || media.filePath || "";
    })
    .filter(Boolean)
    .sort();
}

function hasNewMediaUploads(draft: ItemDraft): boolean {
  return draft.mediaFiles.some((file) => file instanceof File);
}

export function buildItemDraftBaseline(
  item: LatestInspectionItem,
): ItemDraftBaseline {
  const isNumeric =
    item.resultType === ResultType.NUMERIC ||
    item.resultType === ResultType.ODOMETER;
  const report = item.machineReport;

  return {
    result: isNumeric
      ? normalizeNumericResultForPayload(String(item.result ?? ""), {
          inspectionItemId: item.inspectionItemId,
          name: item.name,
          resultType: item.resultType,
          itemCode: item.itemCode ?? null,
          isRequired: item.isRequired ?? false,
        })
      : String(item.result ?? ""),
    comment: report?.reportComment?.trim() ?? "",
    freeText: item.freeText?.trim() ?? "",
    mediaUrls: (report?.machineReportMedias ?? [])
      .map((media) => media.mediaUrl ?? "")
      .filter(Boolean)
      .sort(),
    recordingUrl:
      report?.machineReportRecordings?.[0]?.recordingUrl?.trim() || null,
  };
}

export function isInspectionItemChanged(
  item: CreateInspectionTemplateItem,
  draft: ItemDraft | undefined,
  baseline?: ItemDraftBaseline,
): boolean {
  if (!draft || !baseline) return true;

  if (getComparableResult(item, draft) !== baseline.result) {
    return true;
  }

  if (item.resultType === ResultType.FREE_TEXT) {
    return (draft.selectedFreeText?.trim() ?? "") !== baseline.freeText;
  }

  if (isNumericItem(item)) {
    return false;
  }

  const comment =
    item.resultType === ResultType.FREE_TEXT
      ? draft.selectedFreeText?.trim() || draft.comment.trim()
      : draft.comment.trim();

  if (comment !== baseline.comment) {
    return true;
  }

  if (draft.removedMediaUrls.length > 0) {
    return true;
  }

  if (hasNewMediaUploads(draft)) {
    return true;
  }

  if (JSON.stringify(getDraftMediaUrls(draft)) !== JSON.stringify(baseline.mediaUrls)) {
    return true;
  }

  if (draft.recordedBlob) {
    return true;
  }

  const currentRecordingUrl = draft.existingRecordingUrl?.trim() || null;
  if (currentRecordingUrl !== baseline.recordingUrl) {
    return true;
  }

  return false;
}

async function buildUpdateMediaFiles(
  draft: ItemDraft,
): Promise<MachineReportMediaPayload[]> {
  const existing: MachineReportMediaPayload[] = [];
  const newFiles: File[] = [];

  for (const file of draft.mediaFiles) {
    const media = file as ExistingMediaFile;
    if (file instanceof File && !media.mediaUrl) {
      newFiles.push(file);
      continue;
    }

    if (media.mediaUrl) {
      existing.push({
        fileName: media.fileName || media.name || extractFileNameFromUrl(media.mediaUrl),
        mediaUrl: media.mediaUrl,
        filePath: media.filePath || extractFilePathFromUrl(media.mediaUrl),
        mimeType: media.mimeType || media.type || "application/octet-stream",
        captureMediaAt: media.captureMediaAt || new Date().toISOString(),
      });
    }
  }

  const uploaded = await uploadMediaFiles(newFiles);
  return [...existing, ...uploaded];
}

async function buildUpdateRecordings(
  draft: ItemDraft,
  originalItem?: LatestInspectionItem,
): Promise<MachineReportRecordingPayload[]> {
  if (draft.recordedBlob) {
    return uploadRecording(draft.recordedBlob);
  }

  if (!draft.existingRecordingUrl) {
    return [];
  }

  const originalRecording =
    originalItem?.machineReport?.machineReportRecordings?.[0];

  return [
    {
      fileName:
        originalRecording?.fileName ||
        extractFileNameFromUrl(draft.existingRecordingUrl),
      recordingUrl: draft.existingRecordingUrl,
      filePath:
        originalRecording?.filePath ||
        extractFilePathFromUrl(draft.existingRecordingUrl),
      mimeType: originalRecording?.mimeType || "audio/m4a",
      recordingAt: originalRecording?.recordingAt || new Date().toISOString(),
    },
  ];
}

async function buildUpdateInspectionItemPayload(
  item: CreateInspectionTemplateItem,
  draft: ItemDraft | undefined,
  baseline: ItemDraftBaseline | undefined,
  originalItem: LatestInspectionItem | undefined,
  lastUpdatedSignatureUrl: string | undefined,
): Promise<UpdateInspectionItemPayload> {
  const resultValue = resolveItemResult(item, draft);
  const freeText =
    item.resultType === ResultType.FREE_TEXT
      ? draft?.selectedFreeText?.trim() || undefined
      : undefined;
  const isItemUpdate = isInspectionItemChanged(item, draft, baseline);

  let machineReport: MachineReportPayload | null = null;

  if (!isNumericItem(item) && draft) {
    const machineReportMedias = await buildUpdateMediaFiles(draft);
    const machineReportRecordings = await buildUpdateRecordings(
      draft,
      originalItem,
    );
    const reportComment =
      item.resultType === ResultType.FREE_TEXT && !draft.comment.trim()
        ? freeText || ""
        : draft.comment.trim();

    if (
      shouldIncludeMachineReport(
        machineReportMedias,
        machineReportRecordings,
        reportComment,
        resultValue,
        item.resultType,
      )
    ) {
      machineReport = {
        reportTitle: item.name,
        reportComment,
        machineReportMedias,
        machineReportRecordings,
        ...(lastUpdatedSignatureUrl ? { lastUpdatedSignatureUrl } : {}),
      };
    }
  }

  const payload: UpdateInspectionItemPayload = {
    inspectionItemId: item.inspectionItemId,
    result: isNumericItem(item)
      ? normalizeNumericResultForPayload(draft?.numericValue ?? "", item)
      : resultValue,
    isRequired: item.isRequired,
    resultType: item.resultType,
    isItemUpdate,
    ...(item.itemCode ? { itemCode: item.itemCode } : {}),
    ...(freeText ? { freeText } : {}),
    ...(machineReport ? { machineReport } : {}),
    ...(draft?.inspectionResultId
      ? { inspectionResultId: draft.inspectionResultId }
      : {}),
  };

  return payload;
}

export async function submitUpdateInspection(params: {
  groupId: string;
  machineId: string;
  inspectionId: string;
  inspectionFormId: string;
  items: CreateInspectionTemplateItem[];
  itemDrafts: Record<string, ItemDraft>;
  baselines: Record<string, ItemDraftBaseline>;
  originalItems: LatestInspectionItem[];
  inspectionAt?: string;
  signatureDataUrl: string | null;
}): Promise<{ successMessage?: string }> {
  const {
    groupId,
    machineId,
    inspectionId,
    inspectionFormId,
    items,
    itemDrafts,
    baselines,
    originalItems,
    inspectionAt,
    signatureDataUrl,
  } = params;

  const lastUpdatedSignatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const originalItemsById = Object.fromEntries(
    originalItems.map((item) => [item.inspectionItemId, item]),
  );

  const inspectionItems = await Promise.all(
    items.map((item) =>
      buildUpdateInspectionItemPayload(
        item,
        itemDrafts[item.inspectionItemId],
        baselines[item.inspectionItemId],
        originalItemsById[item.inspectionItemId],
        lastUpdatedSignatureUrl,
      ),
    ),
  );

  const payload: UpdateInspectionPayload = {
    inspectionFormId,
    currentStatus: "POSTED",
    inspectionItems,
    ...(inspectionAt ? { inspectionAt } : {}),
    ...(lastUpdatedSignatureUrl ? { lastUpdatedSignatureUrl } : {}),
  };

  const response = await apiUpdateInspectionFiltered(
    groupId,
    machineId,
    inspectionId,
    payload,
  );

  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}
